import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCopy, faTrash, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import AuthContext from '../context/AuthContext';

const Users = () => {
  const [invitations, setInvitations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'user' });
  const [success, setSuccess] = useState('');
  const [showSuccessTimeout, setShowSuccessTimeout] = useState(null);
  
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Если пользователь не директор, перенаправляем на главную
    if (user && user.role !== 'director') {
      navigate('/templates');
    }
    
    fetchData();
  }, [user, navigate]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Получаем список приглашений
      const invitationsRes = await axios.get('/api/auth/invitations');
      setInvitations(invitationsRes.data);
      
      // Получаем список пользователей (нужно добавить этот endpoint на сервере)
      const usersRes = await axios.get('/api/users');
      setUsers(usersRes.data);
      
      setLoading(false);
    } catch (err) {
      setError('Ошибка при загрузке данных');
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    setNewInvitation({
      ...newInvitation,
      [e.target.name]: e.target.value
    });
  };
  
  const createInvitation = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      console.log('Отправка запроса на создание приглашения:', newInvitation);
      
      const res = await axios.post('/api/auth/invitations', newInvitation);
      console.log('Ответ от сервера:', res.data);
      
      if (!res.data.invitation || !res.data.invitationLink || !res.data.invitation.token) {
        throw new Error('Сервер вернул некорректные данные для приглашения');
      }
      
      // Добавляем новое приглашение в список
      setInvitations([res.data.invitation, ...invitations]);
      
      // Сбрасываем форму
      setNewInvitation({ email: '', role: 'user' });
      
      // Показываем сообщение об успехе
      showSuccess(`Приглашение создано. Ссылка: ${res.data.invitationLink}`);
      
      // Сохраняем ссылку в буфер обмена
      navigator.clipboard.writeText(res.data.invitationLink);
      
      setLoading(false);
    } catch (err) {
      console.error('Ошибка при создании приглашения:', err);
      console.error('Данные ответа:', err.response?.data);
      setError(err.response?.data?.message || 'Ошибка при создании приглашения');
      setLoading(false);
    }
  };
  
  const deleteInvitation = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить это приглашение?')) {
      return;
    }
    
    try {
      console.log('Отправка запроса на удаление приглашения:', id);
      const res = await axios.delete(`/api/auth/invitations/${id}`);
      console.log('Ответ от сервера:', res.data);
      
      // Удаляем приглашение из списка
      setInvitations(invitations.filter(inv => inv._id !== id));
      
      // Показываем сообщение об успехе
      showSuccess('Приглашение успешно удалено');
    } catch (err) {
      console.error('Ошибка при удалении приглашения:', err);
      console.error('Данные ответа:', err.response?.data);
      setError(err.response?.data?.message || 'Ошибка при удалении приглашения');
    }
  };
  
  const copyInvitationLink = (token) => {
    if (!token) {
      setError('Невозможно скопировать ссылку: токен отсутствует');
      return;
    }
    
    const link = `${window.location.origin}/register/${token}`;
    console.log('Копируем ссылку-приглашение:', link);
    
    navigator.clipboard.writeText(link)
      .then(() => {
        showSuccess('Ссылка-приглашение скопирована в буфер обмена');
      })
      .catch(err => {
        console.error('Ошибка при копировании в буфер обмена:', err);
        setError('Не удалось скопировать ссылку');
      });
  };
  
  const showSuccess = (message) => {
    setSuccess(message);
    
    // Очищаем предыдущий таймаут
    if (showSuccessTimeout) {
      clearTimeout(showSuccessTimeout);
    }
    
    // Устанавливаем новый таймаут
    const timeout = setTimeout(() => {
      setSuccess('');
    }, 5000);
    
    setShowSuccessTimeout(timeout);
  };
  
  // Удаление пользователя
  const deleteUser = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return;
    }
    try {
      await axios.delete(`/api/users/${id}`);
      setUsers(users.filter(u => u._id !== id));
      showSuccess('Пользователь успешно удалён');
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при удалении пользователя');
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="container">
      <h1 className="mb-4">Управление пользователями</h1>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      
      <div className="row">
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="h5 mb-0">Создать приглашение</h3>
            </div>
            <div className="card-body">
              <form onSubmit={createInvitation}>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={newInvitation.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="role" className="form-label">Роль</label>
                  <select
                    className="form-select"
                    id="role"
                    name="role"
                    value={newInvitation.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="user">Пользователь</option>
                    <option value="director">Директор</option>
                  </select>
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : (
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                  )}
                  Создать приглашение
                </button>
              </form>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="h5 mb-0">Активные приглашения</h3>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                  </div>
                </div>
              ) : invitations.length === 0 ? (
                <p className="text-muted text-center">Нет активных приглашений</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Роль</th>
                        <th>Истекает</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.filter(inv => !inv.used).map(invitation => (
                        <tr key={invitation._id}>
                          <td>{invitation.email}</td>
                          <td>
                            {invitation.role === 'director' ? 'Директор' : 'Пользователь'}
                          </td>
                          <td>{formatDate(invitation.expiresAt)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => copyInvitationLink(invitation.token)}
                              title="Копировать ссылку"
                            >
                              <FontAwesomeIcon icon={faCopy} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteInvitation(invitation._id)}
                              title="Удалить приглашение"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="card">
        <div className="card-header">
          <h3 className="h5 mb-0">Пользователи системы</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Загрузка...</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <p className="text-muted text-center">Нет зарегистрированных пользователей</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Имя</th>
                    <th>Email</th>
                    <th>Роль</th>
                    <th>Дата регистрации</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        {user.role === 'director' ? 'Директор' : 'Пользователь'}
                      </td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        {user.role !== 'director' && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Удалить пользователя"
                            onClick={() => deleteUser(user._id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;